import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useReveal } from '../../hooks/useReveal';
import { useCountUp } from '../../hooks/useCountUp';

const STATS = [
    { numeric: 20, suffix: '+', label: 'Projects shipped', note: 'Real builds. Real businesses.' },
    { numeric: 2020, label: 'Year established', note: 'Six years engineering growth systems.' },
    { numeric: 100, suffix: '%', label: 'In-house engineering', note: 'No subcontracted noise.' },
    { numeric: 1, label: 'Team, end-to-end', note: 'Strategy, design, build, marketing — same room.' },
];

const ease = [0.22, 1, 0.36, 1];

export function ByTheNumbers() {
    const ref = useReveal();
    return (
        <section
            data-testid="by-the-numbers-section"
            className="relative section overflow-hidden"
            style={{ background: 'var(--bg-deep)' }}
        >
            <div className="absolute inset-0 grid-bg grid-bg-fade opacity-30" />

            <div className="shell relative">
                <div ref={ref} className="reveal grid grid-cols-1 lg:grid-cols-[1fr_1.05fr] gap-10 lg:gap-20 items-end">
                    <div>
                        <p className="eyebrow">Receipts · The honest numbers</p>
                        <h2 className="mt-6 font-display font-semibold text-[2.6rem] sm:text-[3.6rem] lg:text-[4.6rem] leading-[1.02] tracking-[-0.04em] text-white">
                            Numbers,{' '}
                            <span className="text-gradient-cyan">not vibes.</span>
                        </h2>
                    </div>
                    <p className="text-[17px] leading-[1.65] text-slate-300 max-w-md justify-self-start lg:justify-self-end">
                        Most agencies sell promises. We ship. The list of files
                        on this site is the same list our partners can call
                        and verify.
                    </p>
                </div>

                <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
                    {STATS.map((s, i) => (
                        <StatTile key={s.label} {...s} idx={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function StatTile({ numeric, suffix = '', label, note, idx }) {
    const cardRef = useRef(null);
    const inView = useInView(cardRef, { once: true, amount: 0.4 });
    const { ref: countRef, value } = useCountUp(numeric, { duration: 1500 + idx * 150 });
    return (
        <motion.div
            ref={cardRef}
            data-testid={`stat-${idx}`}
            className="card-glass p-6 lg:p-8 relative overflow-hidden"
            initial={{ opacity: 0, y: 28 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: idx * 0.1, ease }}
        >
            <div
                className="absolute -top-1/3 -right-1/4 w-40 h-40 rounded-full opacity-30"
                style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.4), transparent 65%)' }}
            />
            <p className="relative font-mono text-[10px] tracking-[0.24em] uppercase font-medium text-cyan-400">
                §{String(idx + 1).padStart(2, '0')}
            </p>
            <p
                ref={countRef}
                data-testid={`stat-value-${idx}`}
                className="relative mt-5 font-display font-semibold text-[3.6rem] sm:text-[4.4rem] leading-[0.95] tracking-[-0.05em] text-gradient tabular-nums"
            >
                {Math.round(value)}{suffix}
            </p>
            <p className="relative mt-3 font-display font-medium text-[16px] tracking-[-0.015em] text-white">
                {label}
            </p>
            <p className="relative mt-2 text-[13px] leading-[1.5] text-slate-500">
                {note}
            </p>
        </motion.div>
    );
}
